import { Controller } from "@hotwired/stimulus";
import {
  errorDetail,
  formatDate,
  getAllJSON,
  sendJSON,
} from "../api.js";
import { setComboOptions, comboValue, clearCombo } from "../combobox_helpers.js";

// Client complaints: list and order → item → reason cascade. Resolution belongs to the staff panel.
// Only item codes and dictionary values are displayed; no lead contact data or client free text.
export default class extends Controller {
  static targets = [
    "rows",
    "status",
    "form",
    "orderSelect",
    "itemSelect",
    "reasonSelect",
    "formError",
  ];
  static values = {
    url: String, // /api/complaints/ (optional server-side ?status= filter)
    ordersUrl: String, // /api/orders/ (cascade requests ?complaintable=1)
    reasonsUrl: String, // /api/complaint-reasons/ (active dictionary entries for clients)
  };

  static RESOLUTION_LABEL = {
    replaced: "Wymieniono",
    credited: "Zwrot środków (kredyt)",
    rejected: "Odrzucona",
  };

  connect() {
    if (this.hasRowsTarget) this.load();
    this.#loadOrders();
    this.#loadReasons();
    this.#setupOrderCascade();
  }

  disconnect() {
    if (this._onComboboxChange) {
      window.removeEventListener("combobox:change", this._onComboboxChange);
      this._onComboboxChange = null;
    }
  }

  async load() {
    let data;
    try {
      data = await getAllJSON(this.urlValue);
    } catch {
      this.#status("Nie udało się załadować reklamacji. Odśwież stronę.");
      return;
    }
    const complaints = data;
    this.rowsTarget.textContent = "";
    if (!complaints.length) {
      this.#status("Brak zgłoszeń.");
      return;
    }
    this.#status(null);
    const template = document.getElementById("complaint-row-template");
    for (const complaint of complaints) {
      const row = template.content.firstElementChild.cloneNode(true);
      row.querySelector('[data-field="code"]').textContent =
        complaint.order_item_code;
      // Show the dictionary label, with the reason id as a legacy fallback.
      row.querySelector('[data-field="reason"]').textContent =
        complaint.reason_name ?? String(complaint.reason ?? "—");
      row.querySelector('[data-field="created"]').textContent = formatDate(
        complaint.created,
      );

      // Complaints are either open or resolved; there is no pending-replacement state.
      const open = complaint.status === "open";
      const badge = row.querySelector('[data-field="badge"]');
      if (open) {
        badge.className = "ls-badge ls-badge--warm";
        badge.textContent = "otwarta";
      } else {
        badge.className = "ls-badge ls-badge--sent";
        badge.textContent = "rozstrzygnięta";
      }

      const resolutionCell = row.querySelector('[data-field="resolution"]');
      if (!open) {
        resolutionCell.textContent =
          this.constructor.RESOLUTION_LABEL[complaint.resolution] ??
          complaint.resolution;
        if (complaint.resolution_note) {
          const noteEl = document.createElement("span");
          noteEl.className = "ls-text--muted";
          noteEl.style.cssText = "font-size:12px;display:block;margin-top:3px;";
          const noteLabel = document.createElement("span");
          noteLabel.textContent = "Uzasadnienie: ";
          const noteText = document.createElement("span");
          noteText.textContent = complaint.resolution_note;
          noteEl.append(noteLabel, noteText);
          resolutionCell.appendChild(noteEl);
        }
      } else {
        resolutionCell.textContent = "— w rozpatrywaniu";
      }
      this.rowsTarget.appendChild(row);
    }
  }

  // The order → item cascade listens for combobox:change on window.

  async create(event) {
    event?.preventDefault?.();
    const orderItem = comboValue(this.hasItemSelectTarget ? this.itemSelectTarget : null);
    const reason = comboValue(this.hasReasonSelectTarget ? this.reasonSelectTarget : null);
    const preference = this.element.querySelector(
      'input[name="preferred_resolution"]:checked',
    );
    if (!orderItem || !reason || !preference) {
      this.#formError(
        "Wybierz pozycję, powód i preferowane rozstrzygnięcie.",
      );
      return;
    }
    // Submit only the two identifiers and the dictionary preference.
    const res = await sendJSON("POST", this.urlValue, {
      order_item: Number(orderItem),
      reason: Number(reason),
      preferred_resolution: preference.value,
    });
    if (!res.ok) {
      this.#formError(
        errorDetail(res.data, "Nie udało się zgłosić reklamacji."),
      );
      return;
    }
    this.#formError(null);
    await Promise.all([
      clearCombo(this.application, this.hasOrderSelectTarget ? this.orderSelectTarget : null),
      clearCombo(this.application, this.hasItemSelectTarget ? this.itemSelectTarget : null),
      clearCombo(this.application, this.hasReasonSelectTarget ? this.reasonSelectTarget : null),
    ]);
    const pref = this.element.querySelector('input[name="preferred_resolution"]:checked');
    if (pref) pref.checked = false;
    await this.load();
  }

  async #loadOrders() {
    if (!this.hasOrderSelectTarget) return;
    try {
      // The API limits orders to the complaint window and annotates complaint_open.
      this._orders = await getAllJSON(`${this.ordersUrlValue}?complaintable=1`);
    } catch {
      this._orders = [];
      return;
    }
    const opts = this._orders.map(o => ({
      value: o.code,
      label: `Zamówienie #${o.code} · ${formatDate(o.created)}`,
    }));
    await setComboOptions(this.application, this.orderSelectTarget, opts);
  }

  async #loadReasons() {
    if (!this.hasReasonSelectTarget) return;
    let reasons;
    try {
      reasons = await getAllJSON(this.reasonsUrlValue); // Clients receive active reasons only.
    } catch {
      return;
    }
    const opts = reasons.map(r => ({ value: r.id, label: r.name }));
    await setComboOptions(this.application, this.reasonSelectTarget, opts);
  }

  // Refresh item options on order changes within this form only.
  #setupOrderCascade() {
    if (!this.hasItemSelectTarget) return;
    const form = this.hasFormTarget ? this.formTarget : null;
    this._onComboboxChange = async (e) => {
      if (e.detail.name !== "order") return;
      if (form && e.detail.scope !== form) return;
      const orderId = e.detail.value;
      const order = (this._orders ?? []).find(
        (candidate) => String(candidate.code) === String(orderId),
      );
      const items = order?.items ?? [];
      const opts = items.map(item => ({
        value: item.id,
        label: item.complaint_open
          ? `${item.code} — ${item.category_path} (reklamacja w toku)`
          : `${item.code} — ${item.category_path}`,
        // group MUST equal orderId (same type — code string, matching e.detail.value) so the item
        // combobox's depends_on="order" filter passes: o.group === this.groupFilter (strict ===).
        group: orderId,
      }));
      await clearCombo(this.application, this.hasItemSelectTarget ? this.itemSelectTarget : null);
      await setComboOptions(this.application, this.itemSelectTarget, opts);
    };
    window.addEventListener("combobox:change", this._onComboboxChange);
  }

  #formError(message) {
    if (!this.hasFormErrorTarget) return;
    this.formErrorTarget.hidden = message === null;
    if (message !== null) this.formErrorTarget.textContent = message;
  }

  #status(message) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.hidden = message === null;
    if (message !== null) this.statusTarget.textContent = message;
  }
}
